import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "./lib/api";
import { AppProvider, useApp } from "./context/AppContext";
import AppShell from "./components/AppShell";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ComposerPage from "./pages/ComposerPage";
import CalendarPage from "./pages/CalendarPage";
import Approvals from "./pages/Approvals";
import Queue from "./pages/Queue";
import Engage from "./pages/Engage";
import Insights from "./pages/Insights";
import Assets from "./pages/Assets";
import SeoWriter from "./pages/SeoWriter";
import Billing from "./pages/Billing";
import BrandPage from "./pages/BrandPage";
import SettingsPage from "./pages/SettingsPage";
import Tasks from "./pages/Tasks";
import ReportPage from "./pages/ReportPage";
import LinkInBio from "./pages/LinkInBio";
import PublicBio from "./pages/PublicBio";
import Research from "./pages/Research";
import ClientPortal from "./pages/ClientPortal";
import Onboarding from "./pages/Onboarding";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const { refreshOrg, loadClients } = useApp();
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    (async () => {
      try {
        await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
        window.history.replaceState(null, "", window.location.pathname);
        await refreshOrg();
        const clients = await loadClients();
        navigate(clients && clients.length ? "/dashboard" : "/onboarding", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refreshOrg, loadClients]);
  return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Signing you in…</div>;
}

function Protected({ children }) {
  const { user, loading } = useApp();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function ProtectedBare({ children }) {
  const { user, loading } = useApp();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/composer" element={<Protected><ComposerPage /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/queue" element={<Protected><Queue /></Protected>} />
      <Route path="/engage" element={<Protected><Engage /></Protected>} />
      <Route path="/insights" element={<Protected><Insights /></Protected>} />
      <Route path="/assets" element={<Protected><Assets /></Protected>} />
      <Route path="/seo" element={<Protected><SeoWriter /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/brand" element={<Protected><BrandPage /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/bio" element={<Protected><LinkInBio /></Protected>} />
      <Route path="/reports" element={<ProtectedBare><ReportPage /></ProtectedBare>} />
      <Route path="/onboarding" element={<ProtectedBare><Onboarding /></ProtectedBare>} />
      <Route path="/research" element={<Protected><Research /></Protected>} />
      <Route path="/b/:slug" element={<PublicBio />} />
      <Route path="/portal/:token" element={<ClientPortal />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AppProvider>
  );
}
